import React, { useEffect, useState } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { Row, Col } from "react-bootstrap";
import { Clock, CheckCircle } from "react-bootstrap-icons";
import Select from "react-select";
import { ThreeCircles } from "react-loader-spinner";
import './dashboard.css';

const STATUS_COLOR_MAP = {
  "Approved": "#388E3C",         // Green
  "Out For Signature": "#003f5c",   // Teal
  "Draft": "#FFD54F",   // Yellow
  "Partially Approved": "#FF7F00",          // Orange
  "Pending Approval": "#1976D2",          // Dark Blue
  "Rejected": "#D32F2F",         // Red
  "Delivery Failed": "#D32F2F",    // Red
  "E-Signed": "#388E3C",          // Green
};

const fiscalYearDateRanges = {
  FY2023: [new Date("2022-07-01"), new Date("2023-06-30")],
  FY2024: [new Date("2023-07-01"), new Date("2024-06-30")],
  FY2025: [new Date("2024-07-01"), new Date("2025-06-30")],
  "Delivery Failed": "#D32F2F",    // Red
  "E-Signed": "#388E3C",          // Green
};

const NULL_LABEL = "Not Specified";

const useThemeColors = () => {
  const [colors, setColors] = useState({
    primary: '#1976D2',
    tertiary: '#E3F2FD'
  });

  useEffect(() => {
    const root = document.documentElement;
    const styles = getComputedStyle(root);

    const primary = styles.getPropertyValue('--theme1_primary').trim();
    const tertiary = styles.getPropertyValue('--theme1_tertiary').trim();

    setColors({
      primary: primary || '#1976D2',
      tertiary: tertiary || '#E3F2FD'
    });
  }, []);

  return colors;
};

const EL_Dashboard = () => {
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({
    office: { label: "All", value: "All" },
    partner: { label: "All", value: "All" },
    status: { label: "All", value: "All" },
  });
  const [fiscalYear, setFiscalYear] = useState({ label: "All", value: "All" });
  const [offices, setOffices] = useState([]);
  const [partners, setPartners] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [totalMinutesSaved, setTotalMinutesSaved] = useState(0);
  const [successfulELs, setSuccessfulELs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const { primary: themePrimary, tertiary: themeTertiary } = useThemeColors();
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const requestOptions = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        };

        const response = await fetch(
          `${process.env.REACT_APP_BASE_URI}/mdm/engagement-letter/dashboard/`,
          requestOptions
        );

        if (!response.ok) {
          if (response.status === 401) {
            alert("Session ended, please log in again");
            return;
          } else {
            throw new Error("Request failed.");
          }
        }

        const result = await response.json();

        if (!result || !Array.isArray(result.data) || result.data.length === 0) {
          setIsEmpty(true);
          setLoading(false);
          return;
        }

        const normalizedData = result.data.map(item => ({
          ...item,
          City: normalizeValue(item.City),
          FirstName: normalizeValue(item.FirstName),
        }));

        setData(normalizedData);

        const totalSavedMinutes = result.data.reduce((sum, item) => {
          const actualTime = item.timeSpentInMinutes || 0;
          const hasValidSignature = item.SignatureStatus && item.SignatureStatus !== "None";
          return hasValidSignature ? sum + (25 - actualTime) : sum;
        }, 0);

        setTotalMinutesSaved(totalSavedMinutes);
        setTotalMinutesSaved(totalSavedMinutes);
        setSuccessfulELs(result.data.filter(d => d.SignatureStatus === "E-Signed").length);


        const uniqueOffices = ["All", ...new Set(normalizedData.map(d => d.City))];
        const uniquePartners = ["All", ...new Set(normalizedData.map(d => d.FirstName))];
        const uniqueStatuses = ["All", ...new Set(normalizedData.map(d => d.ApprovalStatus))];

        setOffices(uniqueOffices);
        setPartners(uniquePartners);
        setStatuses(uniqueStatuses);
        setIsEmpty(false);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getTotalsByKey = (items, key) => {
    return items.reduce((acc, item) => {
      const k = item[key];
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
  };

  const sortKeysByTotalDesc = (totalsMap) =>
    Object.entries(totalsMap)
      .sort((a, b) => b[1] - a[1])
      .map(([key]) => key);

  const handleFilterChange = (selectedOption, filterType) => {
    setFilters(prev => ({ ...prev, [filterType]: selectedOption }));
  };

  const normalizeValue = (value) =>
    value === null || value === undefined || value === ""
      ? NULL_LABEL
      : value;

  const filteredData = data.filter(d => {
    const createdAt = new Date(d.CreatedDate);
    const [fyStart, fyEnd] = fiscalYearDateRanges[fiscalYear.value] || [null, null];

    return (
      (filters.office.value === "All" || d.City === filters.office.value) &&
      (filters.partner.value === "All" || d.FirstName === filters.partner.value) &&
      (filters.status.value === "All" || d.ApprovalStatus === filters.status.value) &&
      (fiscalYear.value === "All" || (createdAt >= fyStart && createdAt <= fyEnd))
    );
  });

  useEffect(() => {
    const [fyStart, fyEnd] = fiscalYearDateRanges[fiscalYear.value] || [null, null];

    const updatedFilteredData = data.filter(d => {
      const createdAt = new Date(d.CreatedDate);
      return (
        (filters.office.value === "All" || d.City === filters.office.value) &&
        (filters.partner.value === "All" || d.FirstName === filters.partner.value) &&
        (filters.status.value === "All" || d.ApprovalStatus === filters.status.value) &&
        (fiscalYear.value === "All" || (createdAt >= fyStart && createdAt <= fyEnd))
      );
    });

    const totalSaved = updatedFilteredData.reduce((sum, item) => {
      const actualTime = item.timeSpentInMinutes || 0;
      const hasValidSignature = item.ApprovalStatus && item.ApprovalStatus === "Approved";
      return hasValidSignature ? sum + (25 - actualTime) : sum;
    }, 0);

    setTotalMinutesSaved(totalSaved);
    setSuccessfulELs(updatedFilteredData.filter(d => d.SignatureStatus === "E-Signed").length);
  }, [data, filters, fiscalYear]);

  const FIXED_STATUS_ORDER = ["Draft", "Pending Approval", "Partially Approved", "Rejected", "Approved"];

  Highcharts.setOptions({
    lang: {
      noData: "No data available"
    }
  });

  // add to every chart config
  const chartDefaults = {
    lang: { noData: "No data available" },
    noData: {
      style: {
        fontWeight: 'bold',
        fontSize: '16px',
        color: '#6E6E6E'
      }
    }
  };

  Highcharts.setOptions({
    lang: {
      noData: "No data available"
    }
  });

  const statusBarOptions = {
    ...chartDefaults,
    ...chartDefaults,
    chart: { type: "column" },
    title: { text: "EL Approval Status Distribution" },
    xAxis: {
      categories: FIXED_STATUS_ORDER,
      title: { text: "Status" },
      labels: { style: { fontSize: '12px' } }
    },
    yAxis: {
      min: 0,
      title: { text: "Total Count" },
      allowDecimals: false
    },
    legend: { enabled: false },
    tooltip: {
      pointFormat: "<b>{point.y}</b> ELs"
    },
    plotOptions: {
      column: {
        pointWidth: 30
      }
    },
    series: [{
      name: "Count",
      data: FIXED_STATUS_ORDER.map(status => ({
        name: status,
        y: filteredData.filter(d => d.ApprovalStatus === status).length,
        color: STATUS_COLOR_MAP[status] || undefined
      }))
    }]
  };

const signatureCounts = filteredData.reduce((acc, d) => {
  acc[d.SignatureStatus] = (acc[d.SignatureStatus] || 0) + 1;
  return acc;
}, {});

const totalSignatureCount = Object.values(signatureCounts).reduce((sum, val) => sum + val, 0);

const signaturePieOptions = {
    ...chartDefaults,
    ...chartDefaults,
    chart: { type: "pie" },
    title: { text: "EL Signature Status Distribution" },
    tooltip: {
      pointFormat: "{series.name}: <b>{point.y}</b> ({point.percentage:.1f}%)"
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format: "<b>{point.name}</b>: {point.y}"
        }
      }
    },
    legend: {
      enabled: true,
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
      labelFormatter: function () {
        return `Total: ${totalSignatureCount}`;
      }
    },
    series: [{
      name: "Count",
      data: Object.entries(signatureCounts).map(([status, count]) => ({
        name: status,
        y: count,
        color: STATUS_COLOR_MAP[status] || undefined
      }))
    }]
  };

  const partnerTotals = getTotalsByKey(filteredData, "FirstName");
  const filteredPartners = sortKeysByTotalDesc(partnerTotals);

  const partnerStackedBarOptions = {
    ...chartDefaults,
    chart: { 
      type: "bar",
    },
    title: { text: "EL Distribution by Primary Partner and Status" },
    xAxis: {
      categories: filteredPartners,
      title: { text: "Primary Partner" },
      gridLineWidth: 0,
      lineWidth: 0,
      tickWidth: 0,
      min: 0,
      max: 4,
      scrollbar: {
        enabled: true,
        showFull: false,
        barBackgroundColor: themeTertiary,
        barBorderRadius: 6,
        barBorderWidth: 0,

        trackBackgroundColor: themePrimary,
        trackBorderRadius: 6,
        trackBorderWidth: 0,

        rifleColor: '#FFFFFF',

        buttonBackgroundColor: themePrimary,
        buttonBorderWidth: 0,
        buttonBorderRadius: 6,
        buttonArrowColor: themeTertiary,

        mouseWheelEnabled: true,

        states: {
          hover: {
            barBackgroundColor: themeTertiary
          },
          select: {
            barBackgroundColor: themeTertiary
          }
        }
      },
    },
    yAxis: {
      min: 0,
      title: { text: "Total Count" },
      stackLabels: { enabled: true },
    },
    tooltip: {
      headerFormat: "<b>{point.x}</b><br/>",
      pointFormat: "{series.name}: {point.y}<br/>Total: {point.stackTotal}"
    },
    plotOptions: {
      bar: {
        stacking: "normal",
        dataLabels: { enabled: false },
        pointWidth: 30
      }
    },
    series: FIXED_STATUS_ORDER.map(status => ({
      name: status,
      data: filteredPartners.map(partner =>
        filteredData.filter(d => d.FirstName === partner && d.ApprovalStatus === status).length
      ),
      color: STATUS_COLOR_MAP[status] || undefined
    }))
  };

  const officeTotals = getTotalsByKey(filteredData, "City");
  const filteredOffices = sortKeysByTotalDesc(officeTotals);

  const stackedBarOptions = {
    ...chartDefaults,
    chart: { type: "column" },
    title: { text: "Total ELs by Office" },
    xAxis: {
      categories: filteredOffices,
      crosshair: true,
      gridLineWidth: 0,
      lineWidth: 0,
      tickWidth: 0
    },
    yAxis: {
      min: 0,
      title: { text: "Total Count" },
      stackLabels: { enabled: true },
      gridLineWidth: 0,
      lineWidth: 0,
      reversedStacks: false
    },
    tooltip: {
      headerFormat: "<b>{point.x}</b><br/>",
      pointFormat: "{series.name}: {point.y}<br/>Total: {point.stackTotal}"
    },
    plotOptions: {
      column: {
        stacking: "normal",
        dataLabels: { enabled: false },
        pointWidth: 30
      }
    },
    series: FIXED_STATUS_ORDER.map(status => ({
      name: status,
      data: filteredOffices.map(office =>
        filteredData.filter(d => d.City === office && d.ApprovalStatus === status).length
      ),
      color: STATUS_COLOR_MAP[status] || undefined
    }))
  };

  return (
      <>
        {loading && (
          <div className="creatorLoader">
            <ThreeCircles
              height="50"
              width="50"
              color="#4fa94d"
              wrapperStyle={{}}
              wrapperClass=""
              visible={true}
              ariaLabel="three-circles-rotating"
              outerCircleColor="var(--theme1_tertiary)"
              innerCircleColor="var(--theme1_secondary)"
              middleCircleColor="var(--theme1_primary)"
            />
            <div style={{ marginLeft: 10 }}>Loading your data</div>
          </div>
        )}
        <div style={{ padding: "20px" }}>
          <Row>
            <p className="display-6" style={{ height: "fit-content", marginBottom: 0, width: "fit-content" }}>
              Engagement Letter Dashboard
            </p>
          </Row>
        
          {isEmpty ? (
            <div className="text-center mt-5">
              <p>No client data created or currently available.</p>
            </div>
          ) : (
            <>
              {/* Filters */}
              <Row className="mb-4">
                <Col>
                  <label className="form-label move-left">Office</label>
                  <Select
                    placeholder="Select"
                    defaultValue={filters.office}
                    value={filters.office}
                    onChange={e => handleFilterChange(e, "office")}
                    options={offices.map(office => ({ label: office, value: office }))}
                    classNamePrefix="react-select"
                  />
                </Col>
                <Col>
                  <label className="form-label move-left">Primary Partner</label>
                  <Select
                    placeholder="Select"
                    defaultValue={filters.partner}
                    value={filters.partner}
                    onChange={e => handleFilterChange(e, "partner")}
                    options={partners.map(partner => ({ label: partner, value: partner }))}
                    classNamePrefix="react-select"
                  />
                </Col>
                <Col>
                  <label className="form-label move-left">Status</label>
                  <Select
                    placeholder="Select"
                    defaultValue={filters.status}
                    value={filters.status}
                    onChange={e => handleFilterChange(e, "status")}
                    options={statuses.map(status => ({ label: status, value: status }))}
                    classNamePrefix="react-select"
                  />
                </Col>
                <Col>
                  <label className="form-label move-left">Fiscal Year</label>
                  <Select
                    placeholder="Select"
                    value={fiscalYear}
                    onChange={setFiscalYear}
                    options={[
                      { label: "All", value: "All" },
                      { label: "FY2023", value: "FY2023" },
                      { label: "FY2024", value: "FY2024" },
                      { label: "FY2025", value: "FY2025" }
                    ]}
                    classNamePrefix="react-select"
                  />
                </Col>
              </Row>

              {/* Top Banner Row */}
              <Row
                className="p-4 mb-4"
                style={{
                  backgroundColor: "#ffffff",
                  color: "#1A237E",
                  borderRadius: "16px",
                  border: "1px solid #1976D2",
                  boxShadow: "0 4px 12px rgba(25, 118, 210, 0.2)",
                  textAlign: "center"
                }}
              >
                <Col md={6} className="d-flex align-items-center justify-content-center mb-3 mb-md-0">
                  <Clock size={36} className="me-3" color="#1A237E" />
                  <div>
                    <h4 className="mb-0" style={{ color: "#1A237E" }}>{totalMinutesSaved.toFixed(2)}</h4>
                    <small style={{ color: "#37474F" }}>Total Mins Saved processing {totalSignatureCount} ELs</small>
                    <br />
                    <small style={{ color: "#6E6E6E", fontStyle: "italic" }}>* Avg. manual time per EL = 25 mins</small>
                  </div>
                </Col>
                <Col md={6} className="d-flex align-items-center justify-content-center" style={{ borderLeft: "1px solid rgba(25, 118, 210, 0.2)" }}>
                  <CheckCircle size={36} className="me-3" color="#1A237E" />
                  <div>
                    <h4 className="mb-0" style={{ color: "#1A237E" }}>{successfulELs}</h4>
                    <small style={{ color: "#37474F" }}>Engagement Letters Signed</small>
                  </div>
                </Col>
              </Row>

              {/* Charts */}
              <Row className="justify-content-center text-center">
                <Col md={6} className="mb-4 d-flex justify-content-center">
                  <div className="p-3 bg-white rounded shadow-sm w-100">
                    <HighchartsReact highcharts={Highcharts} options={statusBarOptions} />
                  </div>
                </Col>
                <Col md={6} className="mb-4 d-flex justify-content-center">
                  <div className="p-3 bg-white rounded shadow-sm w-100">
                    <HighchartsReact highcharts={Highcharts} options={signaturePieOptions} />
                    <div style={{ textAlign: "center", color: "#6E6E6E", fontSize: "14px" }}>
                      <strong>Total:</strong> {totalSignatureCount}
                    </div>
                  </div>
                </Col>
                <Col md={6} className="mb-4 d-flex justify-content-center">
                  <div className="p-3 bg-white rounded shadow-sm w-100">
                    <HighchartsReact highcharts={Highcharts} options={partnerStackedBarOptions} />
                  </div>
                </Col>
                <Col md={6} className="mb-4 d-flex justify-content-center">
                  <div className="p-3 bg-white rounded shadow-sm w-100">
                    <HighchartsReact highcharts={Highcharts} options={stackedBarOptions} />
                  </div>
                </Col>
              </Row>
            </>
          )}
        </div>
    </>
  );
};

export default EL_Dashboard;
